import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Flex,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { ChevronDown } from "lucide-react";
import store from "../../redux/store";
import { add_receipt, get_receipt_list } from "../../redux/ReceiptNewSlice";

// Validation Schema
const receiptValidationSchema = Yup.object().shape({
  userid: Yup.string().required("Customer selection is required"),
  voucher_date: Yup.date()
    .required("Voucher date is required")
    .max(new Date(), "Voucher date cannot be in the future"),
  amount: Yup.number()
    .required("Amount is required")
    .positive("Amount must be positive")
    .min(0.01, "Amount must be at least 0.01"),
  payment_mode: Yup.string()
    .required("Payment mode is required")
    .oneOf(["1", "2"], "Invalid payment mode"),
  transaction_number: Yup.string().when("payment_mode", {
    is: "2",
    then: (schema) =>
      schema.required("Transaction number is required for online payments"),
    otherwise: (schema) => schema.notRequired(),
  }),
  mode_of_transaction: Yup.string().when("payment_mode", {
    is: "2",
    then: (schema) =>
      schema.required("Mode of transaction is required for online payments"),
    otherwise: (schema) => schema.notRequired(),
  }),
  narration: Yup.string().max(500, "Narration must be less than 500 characters"),
  orderid: Yup.string().required("At least one Order ID is required"),
  bank_id: Yup.string().when("payment_mode", {
    is: "2",
    then: (schema) =>
      schema.required("Bank selection is required for online payments"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const AddReceiptModal = ({ isOpen, onClose, all_customer_list, bank_list, page, rows }) => {
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const toast = useToast();

  // Fetch orders when customer is selected
  const fetchOrdersForCustomer = async (userid) => {
    if (!userid) {
      setOrders([]);
      return;
    }

    setLoadingOrders(true);
    try {
      const formData = new FormData();
      formData.append("action", "order_list");
      formData.append("userid", userid);

      const response = await fetch(
        "https://gaswale.vensframe.com/api/admin/receipt.php",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.success && data.data) {
        setOrders(data.data);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast({
        title: "Error fetching orders",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Handle customer selection change
  const handleCustomerChange = (e, setFieldValue) => {
    const userid = e.target.value;
    setFieldValue("userid", userid);
    setFieldValue("orderid", "");
    setFieldValue("amount", "");
    fetchOrdersForCustomer(userid);
  };

  // Handle order selection and auto-fetch amount
  const handleOrderChange = (e, setFieldValue) => {
    const orderid = e.target.value;
    setFieldValue("orderid", orderid);

    if (orderid) {
      const selectedOrder = orders.find((order) => order.orderid === orderid);
      if (selectedOrder && selectedOrder.details && selectedOrder.details.length > 0) {
        const totalDiscountedAmount = selectedOrder.details.reduce((sum, item) => {
          const val = parseFloat(item.discounted_amount) || 0;
          return sum + val;
        }, 0);
        setFieldValue("amount", totalDiscountedAmount.toFixed(2));
      } else {
        setFieldValue("amount", "");
      }
    } else {
      setFieldValue("amount", "");
    }
  };

  const handleAddReceipt = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        userid: values.userid,
        voucher_date: values.voucher_date,
        amount: parseFloat(values.amount) || 0,
        payment_mode: values.payment_mode,
        transaction_number: values.transaction_number || "",
        mode_of_transaction: values.mode_of_transaction || "",
        narration: values.narration || "",
        orderid: values.orderid,
        bank_id: values.payment_mode === "2" ? values.bank_id : "",
      };

      await store.dispatch(add_receipt(payload)).unwrap();

      toast({
        title: "Receipt added successfully!",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });

      resetForm();
      setOrders([]);
      setCustomerSearchTerm("");
      await store.dispatch(get_receipt_list({ page, limit: rows }));
      onClose();
    } catch (error) {
      toast({
        title: error || "Error adding receipt",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Reset state when modal closes
  const handleClose = () => {
    setOrders([]);
    setCustomerSearchTerm("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="xl" maxW="700px">
        <ModalHeader>Add New Receipt</ModalHeader>
        <ModalBody>
          <Formik
            initialValues={{
              userid: "",
              voucher_date: "",
              amount: "",
              payment_mode: "",
              transaction_number: "",
              mode_of_transaction: "",
              narration: "",
              orderid: "",
              bank_id: "",
            }}
            validationSchema={receiptValidationSchema}
            onSubmit={handleAddReceipt}
          >
            {({ isSubmitting, values, setFieldValue }) => (
              <Form>
                <Flex direction="column" gap={4}>
                  {/* Searchable Customer Dropdown */}
                  <Field name="userid">
                    {({ field, form }) => {
                      const filteredCustomers = all_customer_list?.filter((customer) =>
                        customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase())
                      );

                      return (
                        <FormControl isInvalid={form.errors.userid && form.touched.userid}>
                          <FormLabel>Customer *</FormLabel>
                          <Menu closeOnSelect>
                            <MenuButton
                              as={Button}
                              w="100%"
                              justifyContent="space-between"
                              rightIcon={<ChevronDown />}
                              variant="outline"
                            >
                              {values.userid
                                ? all_customer_list.find((c) => c.id === values.userid)?.name ||
                                  "Select Customer"
                                : "Select Customer"}
                            </MenuButton>
                            <MenuList maxH="250px" overflowY="auto" p={2}>
                              <Input
                                placeholder="Search customer..."
                                size="sm"
                                mb={2}
                                value={customerSearchTerm}
                                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                autoFocus
                              />
                              {filteredCustomers?.length > 0 ? (
                                filteredCustomers.map((customer) => (
                                  <MenuItem
                                    key={customer.id}
                                    onClick={() => {
                                      setFieldValue("userid", customer.id);
                                      handleCustomerChange(
                                        { target: { value: customer.id } },
                                        setFieldValue
                                      );
                                    }}
                                  >
                                    {customer.name}
                                  </MenuItem>
                                ))
                              ) : (
                                <Text fontSize="sm" color="gray.500" p={2}>
                                  No results found
                                </Text>
                              )}
                            </MenuList>
                          </Menu>
                          <FormErrorMessage>{form.errors.userid}</FormErrorMessage>
                        </FormControl>
                      );
                    }}
                  </Field>

                  {/* Order ID */}
                  <Field name="orderid">
                    {({ field, form }) => (
                      <FormControl isInvalid={form.errors.orderid && form.touched.orderid}>
                        <FormLabel>Order *</FormLabel>
                        <Select
                          placeholder={loadingOrders ? "Loading..." : "Select Order"}
                          {...field}
                          onChange={(e) => handleOrderChange(e, setFieldValue)}
                          isDisabled={loadingOrders || !values.userid}
                        >
                          {orders.map((order) => (
                            <option key={order.orderid} value={order.orderid}>
                              {order.orderid}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{form.errors.orderid}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* Voucher Date */}
                  <Field name="voucher_date">
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={form.errors.voucher_date && form.touched.voucher_date}
                      >
                        <FormLabel>Voucher Date *</FormLabel>
                        <Input type="date" {...field} />
                        <FormErrorMessage>{form.errors.voucher_date}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* Amount */}
                  <Field name="amount">
                    {({ field, form }) => (
                      <FormControl isInvalid={form.errors.amount && form.touched.amount}>
                        <FormLabel>Amount *</FormLabel>
                        <Input type="number" {...field} placeholder="Enter amount" />
                        <FormErrorMessage>{form.errors.amount}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* Payment Mode */}
                  <Field name="payment_mode">
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={form.errors.payment_mode && form.touched.payment_mode}
                      >
                        <FormLabel>Payment Mode *</FormLabel>
                        <Select
                          {...field}
                          placeholder="Select Mode"
                          onChange={(e) => {
                            setFieldValue("payment_mode", e.target.value);
                            setFieldValue("transaction_number", "");
                            setFieldValue("mode_of_transaction", "");
                            setFieldValue("bank_id", "");
                          }}
                        >
                          <option value="1">Cash</option>
                          <option value="2">Online</option>
                        </Select>
                        <FormErrorMessage>{form.errors.payment_mode}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* Conditional Fields for Online Mode */}
                  {values.payment_mode === "2" && (
                    <>
                      {/* Mode of Transaction */}
                      <Field name="mode_of_transaction">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.mode_of_transaction && form.touched.mode_of_transaction
                            }
                          >
                            <FormLabel>Mode of Transaction *</FormLabel>
                            <Select {...field} placeholder="Select Mode">
                              <option value="1">Phonepay</option>
                              <option value="2">GPay</option>
                              <option value="3">Paytm</option>
                              <option value="4">Cred</option>
                              <option value="6">RTGS</option>
                              <option value="7">NEFT</option>
                              <option value="8">IMPS</option>
                              <option value="5">others</option>
                            </Select>
                            <FormErrorMessage>{form.errors.mode_of_transaction}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      {/* Transaction Number */}
                      <Field name="transaction_number">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.transaction_number && form.touched.transaction_number
                            }
                          >
                            <FormLabel>Transaction Number *</FormLabel>
                            <Input {...field} placeholder="Enter transaction number" />
                            <FormErrorMessage>{form.errors.transaction_number}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      {/* Bank */}
                      <Field name="bank_id">
                        {({ field, form }) => (
                          <FormControl isInvalid={form.errors.bank_id && form.touched.bank_id}>
                            <FormLabel>Select Bank *</FormLabel>
                            <Select {...field} placeholder="Select Bank">
                              {bank_list.map((bank) => (
                                <option key={bank.value} value={bank.value}>
                                  {bank.bankname} - ({bank.bank_branch}) - {bank.accountno}
                                </option>
                              ))}
                            </Select>
                            <FormErrorMessage>{form.errors.bank_id}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </>
                  )}

                  {/* Narration */}
                  <Field name="narration">
                    {({ field, form }) => (
                      <FormControl isInvalid={form.errors.narration && form.touched.narration}>
                        <FormLabel>Narration</FormLabel>
                        <Input {...field} placeholder="Enter narration (optional)" />
                        <FormErrorMessage>{form.errors.narration}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Button
                    type="submit"
                    colorScheme="green"
                    isLoading={isSubmitting}
                    loadingText="Submitting..."
                  >
                    Submit
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddReceiptModal;